import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Form,
	Input
} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';

import {
	log,
	offsetTimezone
} from '../../services/utils';
import { dateKeyDown } from '../../services/validation';

import HelpModal from '../Shared/HelpModal';
import ValidationPopup from './ValidationPopup';

const FormDatePicker = ( {
	align = 'left',
	disabled,
	errors = [],
	fieldLabel,
	forceValidation,
	helpId,
	icon,
	inputLabel,
	inputLabelPosition = 'left',
	name,
	onChange,
	readOnly,
	required,
	format = 'date',
	value,
	width,
	...props
} ) => {
	const datepickerRef = useRef();
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ inputVal, setInputVal ] = useState( '' );
	const [ validationPopup, setValidationPopup ] = useState( false );

	const hasFocus = useCallback( () => inputRef.current === document.activeElement, [] );
	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);

	const handleSelect = useCallback( date => {
		if ( errors.length ) {
			setValidate( true );
		}

		const target = {
			name,
			value: date && offsetTimezone( date, 'ISO' ).replace( 'Z', '' )
		};

		// update formData
		onChange( { target } );
	}, [
		errors.length,
		name,
		onChange
	] );

	const handleChange = useCallback( date => {
		const target = {
			name,
			value: date && offsetTimezone( date, 'ISO' ).replace( 'Z', '' )
		};

		// update formData
		onChange( { target } );
	}, [
		name,
		onChange
	] );

	const handleKeyDown = useCallback( e => {
		if ( format === 'time' ) {
			e.preventDefault();
		}

		if ( e.key === '/' ) {
			e.preventDefault();
		}
	}, [ format ] );

	const handleInputChange = useCallback( e => {
		// disable validation while user is entering a value
		if ( validate ) {
			setValidate( false );
		}

		// disable validationPopup while user is entering a value
		if ( validationPopup ) {
			setValidationPopup( false );
		}

		const inputValue = e.target.value;

		if ( format === 'date' && inputValue.length === 10 ) {
			handleChange( inputValue );
		} else if ( value ) {
			handleChange( null );
		}

		// datepickerRef.current.setOpen( false );

		setInputVal( prev => {
			if ( format === 'time' ) {
				return inputValue;
			}

			const prevLastChar = prev[ prev.length - 1 ];
			const { length } = inputValue;

			if ( length === 2 || length === 5 ) {
				return prevLastChar !== '/'
					? `${ inputValue }/`
					: inputValue.slice( 0, -1 );
			}

			return inputValue;
		} );
	}, [
		format,
		handleChange,
		validate,
		validationPopup,
		value
	] );

	const moveCursorToEnd = useCallback( () => {
		const { current } = inputRef;
		const { length } = current.value;

		if ( current.setSelectionRange ) {
			current.focus();
			current.setSelectionRange( length, length );
		} else if ( current.createTextRange ) {
			const t = current.createTextRange();

			t.collapse( true );
			t.moveEnd( 'character', length );
			t.moveStart( 'character', length );
			t.select();
		}
	}, [] );

	const handleFocus = useCallback( () => {
		if (
			!validationPopup
			&& isInvalid()
			&& hasFocus()
		) {
			setValidationPopup( true );
		} else if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		hasFocus,
		isInvalid,
		validationPopup
	] );

	const handleBlur = useCallback( () => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		// disable validationPopup when field loses focus
		if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		errors.length,
		validate,
		validationPopup
	] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>
			{ log( `- ${ name } input` ) }
			<Form.Field
				disabled={ disabled }
				id={ name }
				error={ isInvalid() }
				required={ !readOnly && required }
				style={ alignment }
				width={ width }
			>
				<label>
					{ helpId
						? <HelpModal
							id={ helpId }
							triggerPosition="left"
							triggerSize="small"
						/>
						: null
					}
					{ fieldLabel }
				</label>
				<DatePicker
					customInput={
						<Input
							labelPosition={ inputLabelPosition }
							icon={ Boolean( icon ) }
							name={ name }
							readOnly={ readOnly }
							required
							transparent={ readOnly }
							{ ...props }
							autoComplete="chrome-off"
						>
							{ inputLabel || null }
							<input
								maxLength={ format === 'date' && 10 }
								onBlur={ handleBlur }
								onChange={ handleInputChange }
								onFocus={ handleFocus }
								onKeyDown={ handleKeyDown }
								onSelect={ moveCursorToEnd }
								ref={ inputRef }
								style={ format === 'time' ? { caretColor: 'transparent' } : {} }
								value={ value
									? format === 'time'
										? new Date( value ).toLocaleString(
											'en-US',
											{
												hour: 'numeric',
												minute: 'numeric'
											}
										)
										: format === 'dateTime'
											? new Date( value ).toLocaleString(
												'en-US',
												{
													day: '2-digit',
													month: '2-digit',
													year: 'numeric',
													hour: 'numeric',
													minute: 'numeric'
												}
											)
											: new Date( value ).toLocaleString( // format === 'date' (default)
												'en-US',
												{
													day: '2-digit',
													month: '2-digit',
													year: 'numeric'
												}
											)
									: inputVal }
							/>
							{ icon || null }
						</Input>
					}
					showTimeCaption={ false }
					onKeyDown={ e => {
						if ( e.key === 'Tab' ) {
							datepickerRef.current.setOpen( false );
						}

						dateKeyDown( e );
					}}
					onChange={ handleSelect }
					ref={ datepickerRef }
					selected={ value
						? new Date( value )
						: ''
					}
					showTimeInput={
						format === 'time'
						|| format === 'dateTime'
					}
					showTimeSelectOnly={ format === 'time' }
					strictParsing
					timeInputLabel=""
				/>
			</Form.Field>
			<ValidationPopup
				context={ inputRef }
				errors={ errors }
				open={ validationPopup }
			/>
		</>
	);
};

FormDatePicker.propTypes = {
	align: PropTypes.string,
	disabled: PropTypes.bool,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node.isRequired,
	forceValidation: PropTypes.bool,
	helpId: PropTypes.number,
	icon: PropTypes.node,
	inputLabel: PropTypes.node,
	inputLabelPosition: PropTypes.oneOf( [
		'left',
		'right',
		'right corner',
		'left corner'
	] ),
	mask: PropTypes.string,
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	format: PropTypes.oneOf( [ 'date', 'time', 'dateTime' ] ),
	value: PropTypes.oneOfType( [
		PropTypes.string,
		PropTypes.instanceOf( Date )
	] ).isRequired,
	width: PropTypes.number
};

export default memo( FormDatePicker );
